'use client'

import { InputTextField } from '@lastbrand/web-ui/input-text-field'
import { TextLink } from '@lastbrand/web-ui/text-link'
import { css } from '@lastbrand/web-ui-styled-system/css'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, type ChangeEvent } from 'react'

import { EDDMessageContainerDS } from '@src/components/blocks/edd-message-container-ds'
import { waitlistInputCss } from '@src/components/blocks/product-cta-ds/product-cta-ds.styles'
import { ProductDisplayButtonDS } from '@src/components/blocks/product-cta-ds/product-display-button-ds'
import {
  InStockAndReadyToShipDS,
  ProductDisplayTextDS,
  RenderErrorMessageDS,
} from '@src/components/blocks/product-cta-ds/product-message-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { BUTTON_TYPES } from '@src/quince-components/src/atoms/button/button.constants'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import { CTA_TYPE } from '@src/quince-components/src/atoms/productCTA/productCTAConstants'
import { VariantPurchaseInfo } from '@src/quince-components/src/atoms/productForm/ProductForm'
import { PurchaseType } from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import WaitlistSmsModal from '@src/quince-components/src/atoms/waitlist-sms-modal'
import { getProductLink } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { AppQRModal, useShopInAppHandler } from '@src/quince-site-common/src/hooks/appQrModal'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { useMiniPDPProductCTA } from './useMiniPDPProductCTA'

const mobileCTARowCss = css({
  display: 'flex',
  gap: 'space-12',
  '& > *': { flex: 1, minWidth: '100px' },
})

const fullWidthButtonCss = css.raw({ width: 'full' })

const stockMessageWrapperCss = css({
  display: 'flex',
  justifyContent: { base: 'flex-start', lg: 'center' },
  alignItems: 'flex-start',
  marginBottom: 'space-20',
  '& > *': { marginBottom: 0 },
})

const inventoryMessageCss = css({
  textStyle: 'body-14',
  color: 'umber',
  textAlign: 'center',
  marginBottom: 'space-10',
})

const waitlistedTextCss = css({ color: 'red-100' })

const detailsCss = css({
  textAlign: 'center',
  marginTop: 'space-20',
})

const MiniPDPProductCTAContentDS = ({
  product,
  selectedOptions,
  type,
  showInStockAndReadyToShip,
  selectedVariantPurchaseInfo,
  errorMessage,
  setEmail,
  isAppQRModalOpen,
  handleCloseAppQRModal,
  handleClick,
  isAddToCartInProgress,
  isPending,
  disableAddToCart,
  isGoldBarError,
  isCurrentProductOptionLeavingSoon,
}: {
  product: Product
  selectedOptions: ProductVariantOption[]
  type: CTA_TYPE
  selectedVariantPurchaseInfo: VariantPurchaseInfo | undefined
  showInStockAndReadyToShip?: boolean
  errorMessage: string
  setEmail: Dispatch<SetStateAction<string>>
  isAppQRModalOpen: boolean
  handleCloseAppQRModal: () => void
  handleClick: () => void
  isAddToCartInProgress?: boolean
  isPending?: boolean
  disableAddToCart?: boolean
  isGoldBarError?: boolean
  isCurrentProductOptionLeavingSoon?: boolean
}) => {
  const router = useRouter()
  const deviceInfo = useDeviceInfo()
  const { t } = useTranslation('common')
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const productLink = getProductLink(product, selectedOptions)

  const handleFullDetailsClick = () => {
    const variants = productHelper.getVariantsForOptions(product, selectedOptions)
    const selectedVariant = variants[0]
    trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_FULL_DETAILS_VIEWED, {
      context: {
        productProperties: getProductPropertiesForTracking(product, selectedVariant?.svid?.toString(), {}),
      },
    })
    router.push(productLink)
  }

  const renderCTA = () => {
    switch (type) {
      case CTA_TYPE.PREORDER:
        return (
          <ProductDisplayButtonDS
            onClick={handleClick}
            // legacy `||` semantics: isPending === false must not suppress an active ATC loader
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            showLoader={isPending || isAddToCartInProgress}
            buttonCss={fullWidthButtonCss}
          >
            {t('preorder')}
          </ProductDisplayButtonDS>
        )

      case CTA_TYPE.WAITLIST:
        return (
          <ProductDisplayButtonDS
            onClick={handleClick}
            buttonType={isCurrentProductOptionLeavingSoon ? BUTTON_TYPES.PRIMARY : BUTTON_TYPES.SECONDARY}
            showLoader={isAddToCartInProgress}
            buttonCss={fullWidthButtonCss}
          >
            {isCurrentProductOptionLeavingSoon
              ? t(ProductCTAConstants.SHOP_SIMILAR)
              : selectedVariantPurchaseInfo?.hasJoinedWaitList
                ? t(ProductCTAConstants.WAITLISTED)
                : t(ProductCTAConstants.JOIN_WAITLIST)}
          </ProductDisplayButtonDS>
        )

      case CTA_TYPE.OUT_OF_STOCK:
        return (
          <ProductDisplayButtonDS onClick={() => undefined} disabled={true} buttonCss={fullWidthButtonCss}>
            {t('out_of_stock')}
          </ProductDisplayButtonDS>
        )

      case CTA_TYPE.SHOP_IN_THE_APP:
        return (
          <ProductDisplayButtonDS
            onClick={handleClick}
            buttonType={BUTTON_TYPES.SECONDARY}
            buttonCss={fullWidthButtonCss}
          >
            {t('shop_in_the_app')}
          </ProductDisplayButtonDS>
        )

      default:
        return (
          <ProductDisplayButtonDS
            onClick={handleClick}
            // legacy `||` semantics: an explicit false on the first operand must not mask the second
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            showLoader={isPending || isAddToCartInProgress}
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            disabled={disableAddToCart || isGoldBarError}
            buttonCss={fullWidthButtonCss}
          >
            {t(atcLabel)}
          </ProductDisplayButtonDS>
        )
    }
  }

  return (
    <div>
      {showInStockAndReadyToShip ? (
        <div className={stockMessageWrapperCss}>
          <InStockAndReadyToShipDS showIcon={!isDesktop} />
        </div>
      ) : null}
      {type === CTA_TYPE.WAITLIST && !isCurrentProductOptionLeavingSoon && (
        <>
          <ProductDisplayTextDS date={selectedVariantPurchaseInfo?.backInStockDate} className={waitlistedTextCss} />
          {!selectedVariantPurchaseInfo?.hasJoinedWaitList && !AuthUtil.isLoggedIn() && (
            <InputTextField
              css={waitlistInputCss}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setEmail?.(e.target.value)
              }}
              labelPlacement="outside"
              placeholder={t('enter_your_email_address')}
              aria-label={t('enter_your_email_address')}
              type="email"
            />
          )}
        </>
      )}
      {type === CTA_TYPE.WAITLIST && isCurrentProductOptionLeavingSoon && (
        <RenderErrorMessageDS errorMessage={t(ProductCTAConstants.ITEM_NO_LONGER_AVAILABLE)} />
      )}
      {errorMessage && <RenderErrorMessageDS errorMessage={errorMessage} />}
      {type === CTA_TYPE.OUT_OF_STOCK && !errorMessage && <RenderErrorMessageDS errorMessage={t('out_of_stock')} />}
      <div>
        {isDesktop ? (
          renderCTA()
        ) : (
          <div className={mobileCTARowCss}>
            <ProductDisplayButtonDS
              onClick={handleFullDetailsClick}
              buttonType={BUTTON_TYPES.OUTLINED}
              buttonCss={fullWidthButtonCss}
            >
              {t('full_details')}
            </ProductDisplayButtonDS>
            {renderCTA()}
          </div>
        )}
        {isDesktop && (
          <div className={detailsCss}>
            <TextLink
              LinkComponent={Link}
              href={productLink}
              onClick={handleFullDetailsClick}
              aria-label={t('view_full_product_details')}
              size="lg"
              caps
            >
              {t('view_full_details')}
            </TextLink>
          </div>
        )}
      </div>
      <AppQRModal
        isOpen={isAppQRModalOpen}
        product={product}
        selectedOptions={selectedOptions}
        onClose={handleCloseAppQRModal}
      />
    </div>
  )
}

export const MiniPDPProductCTADS = (props: {
  product: Product
  selectedOptions: ProductVariantOption[]
  selectedVariantPurchaseInfo: VariantPurchaseInfo | undefined
  productCTAType: CTA_TYPE
  areAllVariantOOS: boolean
  inputOptionError: string
  productFormErrorMessage: string
  onAddToCart: (isPreorder?: boolean, selectedPlanId?: number) => Promise<void>
  onJoinTheWaitList: (email?: string) => void
  showPurchaseTypeSelector?: boolean
  purchaseOption?: PurchaseType
  selectedPlanId?: number
  isAddToCartInProgress?: boolean
  selectedVariantAvailableInventory: number
  showVariantAvailableInventory: boolean
  disableAddToCart?: boolean
  isCurrentProductOptionLeavingSoon?: boolean
  onClickedShopSimilar?: () => void
}) => {
  const { selectedVariantPurchaseInfo, productCTAType } = props
  const { t } = useTranslation('common')

  const { isAppQRModalOpen, handleShopInApp, handleCloseAppQRModal } = useShopInAppHandler()

  const {
    setEmail,
    showInStockAndReadyToShip,
    errorMessage,
    handleClick,
    showEDDMsg,
    isGoldBarError,
    isPending,
    isBisWaitlistSmsEnabled,
    showSmsModal,
    setShowSmsModal,
    onSubmitPhone,
  } = useMiniPDPProductCTA({
    product: props.product,
    selectedOptions: props.selectedOptions,
    productCTAType,
    selectedVariantPurchaseInfo,
    areAllVariantOOS: props.areAllVariantOOS,
    onAddToCart: props.onAddToCart,
    onJoinWaitList: props.onJoinTheWaitList,
    handleShopInApp: handleShopInApp,
    showPurchaseTypeSelector: props.showPurchaseTypeSelector,
    purchaseOption: props.purchaseOption,
    selectedPlanId: props.selectedPlanId,
    isCurrentProductOptionLeavingSoon: props.isCurrentProductOptionLeavingSoon,
    onClickedShopSimilar: props.onClickedShopSimilar,
  })

  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const eddDetail = selectedVariantPurchaseInfo?.eddDetail

  return (
    <div data-testid={TEST_LOCATORS.MINI_PDP.PRODUCT_CTA}>
      {props.showVariantAvailableInventory && (
        <div className={inventoryMessageCss}>
          {t('hurry_only', { quantity: props.selectedVariantAvailableInventory })}
        </div>
      )}
      {props.productFormErrorMessage && <RenderErrorMessageDS errorMessage={props.productFormErrorMessage} />}
      {showEDDMsg && !isGoldBarError ? (
        <EDDMessageContainerDS
          eddDetail={eddDetail}
          product={props.product}
          selectedInternalVariantId={selectedVariantPurchaseInfo?.selectedInternalVariantId}
          showIcon={!isDesktop}
        />
      ) : null}
      <MiniPDPProductCTAContentDS
        product={props.product}
        selectedOptions={props.selectedOptions}
        type={productCTAType}
        selectedVariantPurchaseInfo={selectedVariantPurchaseInfo}
        showInStockAndReadyToShip={showInStockAndReadyToShip}
        errorMessage={errorMessage}
        setEmail={setEmail}
        handleClick={handleClick}
        isAppQRModalOpen={isAppQRModalOpen}
        handleCloseAppQRModal={handleCloseAppQRModal}
        isAddToCartInProgress={props.isAddToCartInProgress}
        isPending={isPending}
        disableAddToCart={props.disableAddToCart}
        isGoldBarError={isGoldBarError}
        isCurrentProductOptionLeavingSoon={props.isCurrentProductOptionLeavingSoon}
      />
      {isBisWaitlistSmsEnabled ? (
        <WaitlistSmsModal
          open={showSmsModal}
          onClose={() => {
            setShowSmsModal(false)
          }}
          onSubmitPhone={onSubmitPhone}
        />
      ) : null}
    </div>
  )
}
